// src/layout/builders/ProjectTable.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import StatusBadge from "./status/StatusBadge";
import {
  FileText,
  Eye,
  ExternalLink,
  MapPin,
  X,
  UploadCloud,
  Info,
  ArrowUpRightIcon,
  User,
  Calendar,
  DollarSign,
  Building,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import useProjectStatus from "../../store/builders/useProjectStatus";
import useAuthStore from "../../store/useAuthStore";
import { ClipLoader } from "react-spinners";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return "Invalid Date";
  }
};

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return "N/A";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const highlightText = (text, searchTerm) => {
  if (!text) return "";
  const textString = text.toString();
  if (!searchTerm) return textString;
  try {
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedSearchTerm})`, "gi");
    const parts = textString.split(regex);
    return parts.map((part, index) =>
      regex.test(part) && part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={index} className="bg-yellow-200 font-medium">
          {part}
        </span>
      ) : (
        part
      )
    );
  } catch (e) {
    console.error("Highlighting error:", e);
    return textString;
  }
};

const getFileIcon = (fileName) => {
  if (!fileName)
    return <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />;
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (["pdf"].includes(extension))
    return <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />;
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(extension))
    return <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />;
  return <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />;
};

const ProjectTable = ({
  title,
  projects = [],
  handleStatusChange,
  allowedNextStatuses,
  extraActions,
  showActionsColumn = true,
  isCompletedView = false,
  flat = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedHoldProject, setSelectedHoldProject] = useState(null);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const openProjectDetail = (project, tab = "master-data") => {
    if (user?.role === "incharge") {
      sessionStorage.setItem("incharge_active_project_id", String(project.id));
    }
    navigate(`/builder/project/${project.id}/detail?tab=${tab}`);
  };

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [selectedProjectLocation, setSelectedProjectLocation] = useState("");

  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [selectedProjectName, setSelectedProjectName] = useState("");

  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false);
  const [selectedProjectForDocs, setSelectedProjectForDocs] = useState(null);
  const [docsModalData, setDocsModalData] = useState(null);
  const [isFetchingDocs, setIsFetchingDocs] = useState(false);
  const [docsModalError, setDocsModalError] = useState(null);
  const { checkCompletionDocumentsStatus } = useProjectStatus();

  useEffect(() => {
    if (!Array.isArray(projects)) {
      console.error(
        `ProjectTable "${title}" received non-array for projects:`,
        projects
      );
    }
  }, [projects, title]);

  const filteredProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    const searchTerm = searchQuery.trim().toLowerCase();
    if (!searchTerm) return projects;

    return projects.filter(
      (project) =>
        project?.project_name?.toLowerCase().includes(searchTerm) ||
        project?.customer_name?.toLowerCase().includes(searchTerm) ||
        project?.customer_ducktail_id?.toLowerCase().includes(searchTerm) ||
        formatDate(project?.starting_date)
          ?.toLowerCase()
          .includes(searchTerm) ||
        formatDate(
          isCompletedView ? project?.completed_date : project?.deadline_date
        )
          ?.toLowerCase()
          .includes(searchTerm) ||
        formatCurrency(project?.project_budget)
          ?.toLowerCase()
          .includes(searchTerm) ||
        project?.status
          ?.toLowerCase()
          .replace("_", " ")
          .includes(searchTerm.replace("_", " "))
    );
  }, [projects, searchQuery, isCompletedView]);

  const handleViewHoldComments = (project) => {
    setSelectedHoldProject(project);
    setShowHoldModal(true);
  };

  const handleCloseHoldModal = () => {
    setShowHoldModal(false);
    setSelectedHoldProject(null);
  };

  const handleOpenLocationModal = (location) => {
    setSelectedProjectLocation(location || "N/A");
    setIsLocationModalOpen(true);
  };

  const handleCloseLocationModal = () => {
    setIsLocationModalOpen(false);
    setSelectedProjectLocation("");
  };

  const fetchDocsForModal = async (projectId) => {
    setIsFetchingDocs(true);
    setDocsModalError(null);
    setDocsModalData(null);
    try {
      const result = await checkCompletionDocumentsStatus(projectId);
      if (result) {
        setDocsModalData(result);
      } else {
        setDocsModalError("Could not fetch document status. Please try again later.");
      }
    } catch (error) {
      setDocsModalError("An error occurred while fetching document status.");
      console.error("Docs Fetch Error in ProjectTable:", error);
    } finally {
      setIsFetchingDocs(false);
    }
  };

  const handleOpenDocsModal = (project) => {
    setSelectedProjectForDocs(project);
    setIsDocsModalOpen(true);
    fetchDocsForModal(project.id);
  };

  const handleCloseDocsModal = () => {
    setIsDocsModalOpen(false);
    setSelectedProjectForDocs(null);
    setDocsModalData(null);
    setDocsModalError(null);
  };

  const handleNavigateToUpload = () => {
    if (selectedProjectForDocs?.id) {
      navigate(`/upload-doc/${selectedProjectForDocs.id}`);
      handleCloseDocsModal(); // Close the modal after navigating
    }
  };

  const handleOpenNameModal = (name) => {
    setSelectedProjectName(name || "N/A");
    setIsNameModalOpen(true);
  };

  const handleCloseNameModal = () => {
    setIsNameModalOpen(false);
    setSelectedProjectName("");
  };

  // Mobile Card Component
  const ProjectCard = ({ project, index }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 mb-3 shadow-sm hover:shadow-md transition-shadow w-full max-w-full box-border overflow-hidden">
      {/* Project Name Header */}
      <div className="mb-2 min-w-0">
        <h3
          className="text-sm font-semibold text-gray-800 truncate cursor-pointer hover:text-blue-600"
          onClick={() => handleOpenNameModal(project.project_name)}
          title={project.project_name || "View full name"}
        >
          {highlightText(project.project_name, searchQuery)}
        </h3>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <StatusBadge
            project={project}
            handleStatusChange={handleStatusChange}
            allowedNextStatuses={allowedNextStatuses}
          />
        </div>
      </div>

      {/* Project Details Grid */}
      <div className="grid grid-cols-2 gap-x-2 sm:gap-x-3 gap-y-2 mb-3 text-xs w-full">
        <div className="flex items-start gap-1.5 min-w-0 overflow-hidden">
          <User className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 overflow-hidden flex-1">
            <p className="text-gray-500 text-[10px]">Customer</p>
            <p className="font-medium truncate">{project.customer_name || "N/A"}</p>
          </div>
        </div>

        <div className="flex items-start gap-1.5 min-w-0 overflow-hidden">
          <Building className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 overflow-hidden flex-1">
            <p className="text-gray-500 text-[10px]">ID</p>
            <p className="font-medium truncate">{project.customer_ducktail_id || "N/A"}</p>
          </div>
        </div>

        <div className="flex items-start gap-1.5 min-w-0 overflow-hidden">
          <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 overflow-hidden flex-1">
            <p className="text-gray-500 text-[10px]">Location</p>
            <p className="font-medium truncate">{project.project_location || "N/A"}</p>
          </div>
        </div>

        <div className="flex items-start gap-1.5 min-w-0 overflow-hidden">
          <DollarSign className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 overflow-hidden flex-1">
            <p className="text-gray-500 text-[10px]">Budget</p>
            <p className="font-medium truncate">{formatCurrency(project.project_budget)}</p>
          </div>
        </div>

        <div className="flex items-start gap-1.5 min-w-0 overflow-hidden">
          <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 overflow-hidden flex-1">
            <p className="text-gray-500 text-[10px]">Started</p>
            <p className="font-medium truncate">{formatDate(project.starting_date)}</p>
          </div>
        </div>

        <div className="flex items-start gap-1.5 min-w-0 overflow-hidden">
          <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 overflow-hidden flex-1">
            <p className="text-gray-500 text-[10px]">{isCompletedView ? "Completed" : "Deadline"}</p>
            <p className="font-medium truncate">
              {formatDate(
                isCompletedView ? project.completed_date : project.deadline_date
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      {showActionsColumn && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 w-full">
          <button
            onClick={() => openProjectDetail(project, "master-data")}
            className="flex-1 min-w-[120px] px-3 py-2 bg-purple-100 text-purple-600 rounded-md text-xs font-medium hover:bg-purple-200 transition-colors flex items-center justify-center gap-1"
          >
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
            VIEW
          </button>

          {project.status === "hold" && project.holds?.length > 0 && (
            <button
              onClick={() => handleViewHoldComments(project)}
              className="flex-1 min-w-[120px] px-3 py-2 bg-orange-100 text-orange-600 rounded-md text-xs font-medium hover:bg-orange-200 transition-colors flex items-center justify-center gap-1"
            >
              <Eye className="w-3 h-3 flex-shrink-0" />
              Comments
            </button>
          )}

          {extraActions && extraActions(project)}
        </div>
      )}
    </div>
  );

  return (
    <div className={`${flat ? '' : 'bg-white shadow rounded-lg'} p-2 sm:p-3 lg:p-4 relative w-full max-w-full overflow-hidden`} style={flat ? {} : { marginTop: "-15px" }}>
      <div className="flex flex-col sm:flex-row items-center justify-between mb-3 sm:mb-4 gap-2 sm:gap-3 w-full">
        <h2 className="text-sm sm:text-base font-black text-gray-950 uppercase tracking-wide whitespace-nowrap">
          {title}
        </h2>
        <input
          type="text"
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 w-full sm:w-auto max-w-xs text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
        />
      </div>

      {/* Mobile Card View */}
      <div className="block lg:hidden w-full max-w-full overflow-hidden">
        {Array.isArray(filteredProjects) && filteredProjects.length > 0 ? (
          <div className="space-y-3 w-full">
            {filteredProjects.map((project, index) => (
              <ProjectCard key={project.id || project._id || `project-${index}`} project={project} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 text-sm">
            {searchQuery
              ? "No projects found matching your search."
              : projects.length === 0
                ? "No projects available for this status."
                : "No projects found."}
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        {Array.isArray(filteredProjects) && filteredProjects.length > 0 ? (
          <table className="w-full table-auto border-collapse text-xs">
            <thead>
              <tr className="text-center font-black text-gray-950 border-b border-gray-200 bg-gray-50">
                <th className="py-2 px-3 text-left whitespace-nowrap sticky left-0 bg-gray-50 z-10 min-w-[120px] max-w-[120px]">
                  Project Name
                </th>
                <th className="py-2 px-3 whitespace-nowrap">Customer Name</th>
                <th className="py-2 px-3 whitespace-nowrap">Customer ID</th>
                <th className="py-2 px-3 max-w-[150px]">Location</th>
                <th className="py-2 px-3 whitespace-nowrap">Budget</th>
                <th className="py-2 px-3 whitespace-nowrap">Started</th>
                <th className="py-2 px-3 whitespace-nowrap">
                  {isCompletedView ? "Date of Completion" : "Deadline"}
                </th>
                <th className="py-2 px-1 pr-1 whitespace-nowrap">Status</th>
                {showActionsColumn && (
                  <th className="py-2 pl-1 whitespace-nowrap sticky right-0 bg-gray-50 z-10 min-w-[150px]">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project, index) => (
                <tr
                  key={project.id || project._id || `project-${index}`}
                  className="text-gray-700 border-b border-gray-100 hover:bg-gray-50/50 text-center transition-colors duration-150 group"
                >
                  <td className="py-2.5 px-3 text-left whitespace-nowrap sticky left-0 bg-white group-hover:bg-gray-50/50 z-10 max-w-[120px]">
                    <button
                      onClick={() => handleOpenNameModal(project.project_name)}
                      className="w-full text-left truncate hover:text-blue-600 focus:outline-none"
                      title={project.project_name || "View full name"}
                    >
                      {highlightText(project.project_name, searchQuery)}
                    </button>
                  </td>
                  <td className="py-2.5 px-3 text-center whitespace-nowrap">
                    {highlightText(project.customer_name, searchQuery)}
                  </td>
                  <td className="py-2.5 px-3 text-center whitespace-nowrap">
                    {highlightText(project.customer_ducktail_id, searchQuery)}
                  </td>
                  <td className="py-2.5 px-3 text-center max-w-[150px]">
                    <div className="w-full truncate">
                      <span
                        className="truncate cursor-help"
                        title={project.project_location || "N/A"}
                      >
                        {project.project_location || "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-center whitespace-nowrap">
                    {highlightText(
                      formatCurrency(project.project_budget),
                      searchQuery
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-center whitespace-nowrap">
                    {highlightText(
                      formatDate(project.starting_date),
                      searchQuery
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-center whitespace-nowrap">
                    {highlightText(
                      formatDate(
                        isCompletedView
                          ? project.completed_date
                          : project.deadline_date
                      ),
                      searchQuery
                    )}
                  </td>
                  <td className="py-1.5 px-1 pr-1 relative">
                    <div className="flex justify-center">
                      <StatusBadge
                        project={project}
                        handleStatusChange={handleStatusChange}
                        allowedNextStatuses={allowedNextStatuses}
                      />
                    </div>
                  </td>
                  {showActionsColumn && (
                    <td className="py-2 pl-1 sticky right-0 bg-white group-hover:bg-gray-50/50 z-10">
                      <div className="flex justify-center items-center h-full gap-1 flex-wrap">
                        <button
                          onClick={() => openProjectDetail(project, "master-data")}
                          title="View Project Details"
                          className="px-2 py-1 bg-purple-100 text-purple-600 rounded text-[10px] font-medium hover:bg-purple-200 transition-colors duration-200 flex items-center justify-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />{" "}
                          VIEW
                        </button>

                        {project.status === "hold" &&
                          project.holds?.length > 0 && (
                            <button
                              onClick={() => handleViewHoldComments(project)}
                              title="View Hold Comments"
                              className="px-2 py-1 text-[10px] bg-orange-100 text-orange-600 rounded hover:bg-orange-200 transition-colors duration-200 flex items-center justify-center gap-1"
                            >
                              <Eye className="w-3 h-3 flex-shrink-0" /> Comments
                            </button>
                          )}
                        {extraActions && extraActions(project)}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-gray-500 text-sm">
            {searchQuery
              ? "No projects found matching your search."
              : projects.length === 0
                ? "No projects available for this status."
                : "No projects found."}
          </div>
        )}
      </div>

      {/* Docs Status Modal (Completion Docs) */}
      <AnimatePresence>
        {isDocsModalOpen && selectedProjectForDocs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4"
            onClick={handleCloseDocsModal} // Click overlay to close
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg relative flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()} // Prevent closing on inner click
            >
              <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-4 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Completion Documents
                </h3>
                <button
                  onClick={handleCloseDocsModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                  aria-label="Close documents modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="text-xs text-gray-500 mb-3">
                Project: {selectedProjectForDocs.project_name || "N/A"}
              </div>
              <div className="flex-grow overflow-y-auto pr-2 -mr-2 custom-scrollbar">
                {isFetchingDocs ? (
                  <div className="flex justify-center items-center h-40">
                    <ClipLoader size={30} color={"#3B82F6"} />
                    <span className="ml-2 text-gray-500 text-sm">
                      Loading documents...
                    </span>
                  </div>
                ) : docsModalError ? (
                  <div className="text-center py-6 px-4 bg-red-50 text-red-700 rounded-md border border-red-200 text-sm">
                    {docsModalError}
                  </div>
                ) : docsModalData?.documents &&
                  docsModalData.documents.length > 0 ? (
                  <div className="space-y-3">
                    {docsModalData.documents.map((doc, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 text-xs"
                      >
                        <div className="flex items-center gap-2 flex-grow mr-2">
                          {getFileIcon(doc.url)}
                          <span className="font-medium text-gray-700">
                            {doc.name}
                          </span>
                          {doc.required && !isCompletedView && (
                            <span className="text-red-500 text-[10px] font-bold">
                              *Required
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {doc.uploaded ? (
                            <span className="text-green-600 font-semibold text-[10px] bg-green-100 px-1.5 py-0.5 rounded-full">
                              Uploaded
                            </span>
                          ) : (
                            <span className="text-orange-600 font-semibold text-[10px] bg-orange-100 px-1.5 py-0.5 rounded-full">
                              Missing
                            </span>
                          )}
                          {doc.uploaded && doc.url ? (
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-[10px] font-medium flex items-center gap-0.5"
                              title={`View ${doc.name}`}
                            >
                              View/Download{" "}
                              <ArrowUpRightIcon className="w-2.5 h-2.5" />
                            </a>
                          ) : !doc.uploaded ? (
                            <span className="text-gray-400 text-[10px] italic">
                              Not available
                            </span>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-6 text-sm">
                    No document information available for this project.
                  </p>
                )}
              </div>
              <div className="mt-5 pt-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-3 flex-shrink-0">
                <p className="text-xs text-gray-500 flex-grow">
                  {isCompletedView
                    ? "Documents submitted for project completion."
                    : (docsModalData?.allRequiredUploaded
                      ? "All required documents have been uploaded. You can update them if needed."
                      : "Some required documents are missing. Please upload them.")
                  }
                </p>
                {!isCompletedView ? ( // Only show upload button if not in completed view
                  <button
                    onClick={handleNavigateToUpload}
                    className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium flex items-center justify-center gap-1.5"
                  >
                    <UploadCloud className="w-4 h-4" />
                    {docsModalData?.documents?.some(doc => doc.uploaded) ? "Update Documents" : "Upload Documents"}
                  </button>
                ) : ( // Show Close button for completed view
                  <button
                    onClick={handleCloseDocsModal}
                    className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-xs font-medium"
                  >
                    Close
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Location Modal */}
      <AnimatePresence>
        {isLocationModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            onClick={handleCloseLocationModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-lg p-5 max-w-sm w-full shadow-xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleCloseLocationModal}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                aria-label="Close location popup"
              >
                <X className="w-5 h-5" />
              </button>
              <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-500" /> Project Location
              </h3>
              <p className="text-sm text-gray-700 break-words">
                {selectedProjectLocation}
              </p>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleCloseLocationModal}
                  className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-xs font-medium"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Project Name Modal */}
      <AnimatePresence>
        {isNameModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            onClick={handleCloseNameModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-lg p-5 max-w-md w-full shadow-xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleCloseNameModal}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                aria-label="Close project name popup"
              >
                <X className="w-5 h-5" />
              </button>
              <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500" /> Full Project Name
              </h3>
              <p className="text-sm text-gray-700 break-words leading-relaxed">
                {selectedProjectName}
              </p>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleCloseNameModal}
                  className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-xs font-medium"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hold Comments History Modal */}
      {showHoldModal && selectedHoldProject && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 sm:p-6 w-full max-w-lg shadow-xl transform transition-all max-h-[85vh] flex flex-col border border-gray-200">
            <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
                Hold Comments History
              </h3>
              <button
                onClick={handleCloseHoldModal}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                aria-label="Close comment history"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4 text-xs text-gray-600 bg-gray-50 p-3 rounded-md border border-gray-100">
              <p>
                <span className="font-medium text-gray-700">Project:</span>{" "}
                {selectedHoldProject.project_name || "N/A"}
              </p>
              <p>
                <span className="font-medium text-gray-700">Location:</span>{" "}
                {selectedHoldProject.project_location || "N/A"}
              </p>
              <p>
                <span className="font-medium text-gray-700">Customer:</span>{" "}
                {selectedHoldProject.customer_name || "N/A"} (
                {selectedHoldProject.customer_ducktail_id || "N/A"})
              </p>
            </div>
            <div className="flex-grow overflow-y-auto border-t border-gray-200 divide-y divide-gray-100 pr-2 -mr-2 custom-scrollbar">
              {selectedHoldProject.holds &&
                selectedHoldProject.holds.length > 0 ? (
                selectedHoldProject.holds.map((hold, index) => (
                  <div key={hold.id || `hold-${index}`} className="py-3 px-1">
                    <p className="text-gray-800 text-sm leading-relaxed">
                      {hold.comment}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-1.5">
                      {new Date(hold.created_at).toLocaleString("en-GB", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-6 text-sm">
                  No hold comments found for this project.
                </p>
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleCloseHoldModal}
                className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-xs font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #aaa;
        }
      `}</style>
    </div>
  );
};

export default ProjectTable;